/**
 * Order-level "trailer arrived, pending check-in" rules (Trailer Arrival
 * Design): which of an order's legs have a trailer EAPI reported at the yard
 * that nobody has checked in yet, and how order surfaces (Orders list, order
 * card, detail page, side sheet) word it. The trailer side (the 'arrived'
 * status, check-in) lives in trailer-yard-ops.ts.
 */
import type { OrderLegStatusSummary } from "@/types/tms";
import { resolveLegStatus } from "@/lib/leg-status-resolve";
import { isTrailerArrived } from "@/lib/trailer-yard-ops";

/**
 * One leg of an order (its leg summary) plus the two values the
 * "trailer arrived" annotations derive for it.
 */
export type LegWithArrivedTrailer = OrderLegStatusSummary & {
  /** The leg trailer's trailers.current_location_status. */
  trailerLocationStatus: string | null;
  /** The leg's own status from resolveLegStatus (src/lib/leg-status-resolve.ts). */
  legStatus: string | null;
};

/**
 * An order's leg summaries with each leg's trailer location status (looked up
 * by trailer number) and resolved leg status added.
 */
export function toLegsWithArrivedTrailer(
  legs: OrderLegStatusSummary[],
  parentStatus: string | null | undefined,
  trailerLocationStatusById: (trailerId: string) => string | null | undefined,
): LegWithArrivedTrailer[] {
  const isMultiLeg = legs.length > 1;
  return legs.map((l) => ({
    ...l,
    trailerLocationStatus: l.trailer_id ? trailerLocationStatusById(l.trailer_id) ?? null : null,
    legStatus: resolveLegStatus(l, parentStatus, isMultiLeg) ?? null,
  }));
}

/** Leg statuses before the trip leaves (anything before in_transit). */
const PRE_DEPARTURE_LEG_STATUSES = new Set(["booked", "confirmed", "tender_received"]);

/**
 * Legs whose trailer is arrived (pending check-in) and whose trip hasn't left
 * yet. Once a leg is in transit, delivered or canceled the reminder no longer
 * applies to it. A leg with no resolved status is treated as not started.
 */
function arrivedPendingLegs<T extends LegWithArrivedTrailer>(legs: T[]): T[] {
  return legs.filter(
    (l) =>
      isTrailerArrived({ current_location_status: l.trailerLocationStatus }) &&
      (!l.legStatus || PRE_DEPARTURE_LEG_STATUSES.has(l.legStatus)),
  );
}

/**
 * Order-level "pending check-in" label, or null when no leg qualifies
 * (arrivedPendingLegs). A multi-leg order names the legs.
 */
export function arrivedLegsLabel(legs: LegWithArrivedTrailer[]): string | null {
  const arrived = arrivedPendingLegs(legs);
  if (arrived.length === 0) return null;
  if (legs.length <= 1) return "Trailer arrived · pending check-in";
  const legWord = arrived.length === 1 ? "Leg" : "Legs";
  return `${legWord} ${arrived.map((l) => l.leg_sequence ?? "?").join(", ")} trailer arrived · pending check-in`;
}

/**
 * Which trailers arrived, for the order notice's secondary line: "Trailer
 * H20656" on a single-leg order, "Leg 2 trailer H20656, Leg 3 trailer X" on a
 * multi-leg one. Null when no leg qualifies (arrivedPendingLegs).
 */
export function arrivedTrailersText(legs: LegWithArrivedTrailer[]): string | null {
  const arrived = arrivedPendingLegs(legs).filter((l) => !!l.trailer_id);
  if (arrived.length === 0) return null;
  if (legs.length <= 1) return `Trailer ${arrived[0].trailer_id}`;
  return arrived.map((l) => `Leg ${l.leg_sequence ?? "?"} trailer ${l.trailer_id}`).join(", ");
}
